import { HttpClient } from "@angular/common/http";
import { computed, inject, Injectable, signal } from "@angular/core";
import i18next from "i18next";

@Injectable({
  providedIn: 'root'
})
export class I18n {
  lang = signal('de');
  namespaces = new Set<string>();
  loadedNamespaces = signal(new Set<string>());
  loadedCombinations = new Set<string>();

  http = inject(HttpClient);

  async fetchTranslations(language: string, namespace: string) {
    return new Promise((res, rej) => {
      try {
        this.http.get(`locale/${language}/${namespace}.json`).subscribe({
          next(data) {
            res(data);
          },
          error(err) {
            rej(err);
          }
        });
      } catch (err) {
        rej(err);
      }
    });
  }

  async addResources(language: string, namespace: string) {
    if (!this.loadedCombinations.has(`${language}/${namespace}`)) {
      this.loadedCombinations.add(`${language}/${namespace}`);
      i18next.addResourceBundle(language, namespace, await this.fetchTranslations(language, namespace));
    }
  }

  async setLang(lang: string) {
    // assuming the user selects the preferred language early when using the app, the additional namespaces to load should be few
    await Promise.all(['common', ...this.namespaces].map(namespace => this.addResources(lang, namespace)));

    await i18next.changeLanguage(lang);
    this.lang.set(lang);
  }

  async addNamespace(namespace: string) {
    if (!this.namespaces.has(namespace)) {
      this.namespaces.add(namespace);
      await this.addResources(this.lang(), namespace);
      this.loadedNamespaces.update((set) => new Set(...set, namespace));
    }
  }

  translate(key: string, options: Record<string, any>|undefined = undefined) {
    return computed(() => {
      // Even though this.lang is not used here, the resulting signal should emit when this.lang is updated. Same for loadedNamespaces.
      this.lang();
      this.loadedNamespaces();
      return i18next.t(key, options);
    });
  }
}
